import { useTranslation } from "react-i18next";
import { MicOff } from "lucide-react";
import { useNotHeardStore } from "../store/notHeardStore";

/**
 * The one top-of-screen call status banner (#1178, #2224 AC33).
 *
 * Two conditions can hold at once -- the call is reconnecting (#2223), and
 * this speaker is not being heard (#2224) -- and they are one story, so they
 * render as ONE banner, never a pill and a warning side by side:
 *  - reconnecting only:  "Reconnecting…" + Leave (the original pill);
 *  - both:               "You're not being heard, reconnecting…" + Leave;
 *  - not heard only:     "You're not being heard", plus "Reconnect audio"
 *                        once the automatic recovery has given up (AC26).
 *
 * Both the signed-in call and the guest page render it, each passing its own
 * reconnect state (AC34).
 *
 * Fixed dark pill regardless of site theme -- the text/border/spinner must
 * stay fixed-light to match. (`--bg-elevated` isn't a real design token,
 * and the theme-aware `--text-primary`/`--border-hover` flip to near-black in
 * light mode, which made the old pill's text unreadable.)
 */
export function NotHeardBanner({
  reconnecting,
  onLeave,
}: {
  reconnecting: boolean;
  /** Leave the call (offered only while reconnecting). */
  onLeave?: () => void;
}) {
  const { t } = useTranslation();
  const warning = useNotHeardStore((s) => s.warning);
  const failed = useNotHeardStore((s) => s.step === "failed");
  const requestRetry = useNotHeardStore((s) => s.requestRetry);

  if (!reconnecting && !warning) return null;

  const text = reconnecting
    ? warning
      ? t("call.notHeard.reconnecting")
      : t("call.connection.reconnectingTitle")
    : t("call.notHeard.title");

  return (
    <div
      data-testid={reconnecting ? "call-reconnecting-indicator" : "call-not-heard-banner"}
      data-not-heard={warning || undefined}
      role="status"
      aria-live="polite"
      className="fixed left-1/2 top-4 -translate-x-1/2 flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium shadow-lg"
      style={{
        zIndex: 9999,
        background: "#1f2430",
        color: "#fff",
        border: "1px solid rgba(255,255,255,0.15)",
      }}
    >
      {reconnecting ? (
        <span
          className="inline-block h-4 w-4 animate-spin rounded-full border-2"
          style={{ borderColor: "rgba(255,255,255,0.3)", borderTopColor: "var(--accent)" }}
        />
      ) : (
        <MicOff className="h-4 w-4 shrink-0" style={{ color: "#f87171" }} aria-hidden />
      )}
      {text}
      {!reconnecting && failed && (
        <button
          type="button"
          data-testid="call-reconnect-audio"
          onClick={requestRetry}
          className="ms-1 rounded-full px-3 py-1 text-xs font-semibold"
          style={{ background: "rgba(255,255,255,0.12)", color: "#fff" }}
        >
          {t("call.notHeard.reconnectAudio")}
        </button>
      )}
      {reconnecting && onLeave && (
        <button
          type="button"
          data-testid="call-reconnect-leave"
          onClick={onLeave}
          className="ms-1 rounded-full px-3 py-1 text-xs font-semibold"
          style={{ background: "rgba(255,255,255,0.12)", color: "#fff" }}
        >
          {t("call.connection.leave")}
        </button>
      )}
    </div>
  );
}
